import type { EventFormOptions, EventStatus } from '../types';
import { formatRupees } from './event-utils';

export const EVENT_MODULE_KEYS = [
  'registration',
  'fees',
  'questions',
  'dropPoints',
  'whatsapp',
  'cta',
  'highlights',
  'roster',
] as const;

export type EventModuleKey = (typeof EVENT_MODULE_KEYS)[number];
export type EventModules = Record<EventModuleKey, boolean>;
export type EventModuleFlags = Partial<EventModules>;

export const MODULE_LABELS: Record<EventModuleKey, string> = {
  registration: 'Registration',
  fees: 'Fees & payment',
  questions: 'Extra form questions',
  dropPoints: 'Drop points & pickup',
  whatsapp: 'WhatsApp group',
  cta: 'App call to action',
  highlights: 'Past-event highlights',
  roster: 'Roster',
};

export function emptyEventModules(registration = true): EventModules {
  return {
    registration,
    fees: false,
    questions: false,
    dropPoints: false,
    whatsapp: false,
    cta: false,
    highlights: false,
    roster: false,
  };
}

/** Short facts for an event row. Empty sections are left out. */
export function eventSummaryChips(event: EventModuleSource): string[] {
  const opts = event.formOptions;
  const chips: string[] = [];
  if (opts?.memberChoice && (event.feeMember != null || event.feeNonMember != null)) {
    const parts = [
      event.feeMember != null ? `Members ${formatRupees(event.feeMember)}` : null,
      event.feeNonMember != null ? `Others ${formatRupees(event.feeNonMember)}` : null,
    ].filter((part): part is string => part != null);
    if (parts.length > 0) chips.push(parts.join(' · '));
  } else if (event.feeNonMember != null) {
    chips.push(formatRupees(event.feeNonMember));
  }
  if (opts?.paymentRequired) chips.push('Payment');
  if (opts?.collectDob && opts.minAgeYears != null) chips.push(`DOB ${opts.minAgeYears}+`);
  else if (opts?.collectDob) chips.push('DOB');
  if (filled(opts?.whatsappGroupUrl) || opts?.whatsappGroupPublic === true) chips.push('WhatsApp');
  const drops = event.dropPoints?.length ?? 0;
  if (drops > 0) chips.push(drops === 1 ? '1 drop point' : `${drops} drop points`);
  if (event.pickupEnabled === true) chips.push('Pickup');
  return chips;
}

/** Upcoming before today, happening now on the event's calendar day, past after that. */
export function statusFromDate(value: string, now = new Date()): EventStatus {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'upcoming';
  const day = (d: Date) => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy.getTime();
  };
  const eventDay = day(date);
  const today = day(now);
  if (eventDay > today) return 'upcoming';
  if (eventDay < today) return 'past';
  return 'active';
}

export interface EventModuleSource {
  registrationNotRequired?: boolean;
  deadlineAt?: string | null;
  capacity?: number | null;
  feeMember?: number | null;
  feeNonMember?: number | null;
  upiId?: string | null;
  upiQrUrl?: string | null;
  ctaMode?: string;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  coordinator?: string | null;
  specialMention?: string | null;
  participantCountOverride?: number | null;
  detailsUrl?: string | null;
  detailsLabel?: string | null;
  galleryAlbumId?: string | null;
  participants?: unknown[] | null;
  dropPoints?: unknown[] | null;
  pickupEnabled?: boolean;
  formOptions?: Partial<EventFormOptions>;
}

function filled(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

/** On/off from stored fields only. Empty sections the admin left open are not included. */
export function impliedEventModules(event: EventModuleSource): EventModules {
  const opts = event.formOptions;
  return {
    registration: event.registrationNotRequired !== true,
    fees: Boolean(
      opts?.paymentRequired ||
        opts?.memberChoice ||
        filled(opts?.paymentInstructions) ||
        event.feeMember != null ||
        event.feeNonMember != null ||
        filled(event.upiId) ||
        filled(event.upiQrUrl),
    ),
    questions: Boolean(
      opts?.collectDob ||
        opts?.collectGender ||
        opts?.teamSize ||
        filled(opts?.notesQuestion) ||
        opts?.minAgeYears != null,
    ),
    dropPoints: (event.dropPoints?.length ?? 0) > 0 || event.pickupEnabled === true,
    whatsapp: filled(opts?.whatsappGroupUrl) || opts?.whatsappGroupPublic === true,
    cta: (event.ctaMode != null && event.ctaMode !== 'none') || filled(event.ctaLabel) || filled(event.ctaUrl),
    highlights:
      filled(event.coordinator) ||
      filled(event.specialMention) ||
      event.participantCountOverride != null ||
      filled(event.detailsUrl) ||
      filled(event.detailsLabel) ||
      filled(event.galleryAlbumId),
    roster: (event.participants?.length ?? 0) > 0,
  };
}

function savedModuleFlags(options: Partial<EventFormOptions> | undefined): EventModuleFlags {
  const raw = options?.modules;
  if (!raw) return {};
  const flags: EventModuleFlags = {};
  for (const key of EVENT_MODULE_KEYS) {
    if (raw[key] === true) flags[key] = true;
  }
  return flags;
}

/**
 * A section is on when the event already has its data, or when the admin left it
 * on before filling it in (`formOptions.modules`).
 */
export function eventModules(event: EventModuleSource): EventModules {
  const implied = impliedEventModules(event);
  const saved = savedModuleFlags(event.formOptions);
  const modules = emptyEventModules(false);
  for (const key of EVENT_MODULE_KEYS) {
    modules[key] = implied[key] || saved[key] === true;
  }
  return modules;
}

export type RegistrationMode = 'open' | 'closed' | 'coming_soon' | 'not_required';

/** Editor fields that decide whether a section already holds data. */
export interface EditorModuleInput {
  registrationMode: RegistrationMode;
  deadlineAt: string;
  capacity: string;
  feeMember: string;
  feeNonMember: string;
  upiId: string;
  upiQrUrl: string;
  ctaMode: string;
  ctaLabel: string;
  ctaUrl: string;
  coordinator: string;
  specialMention: string;
  participantCountOverride: string;
  detailsUrl?: string;
  detailsLabel?: string;
  galleryAlbumId?: string;
  teamEnabled: boolean;
  notesEnabled: boolean;
  dropPoints: unknown[];
  pickupEnabled: boolean;
  formOptions: EventFormOptions;
  modules: EventModules;
}

export function impliedModulesFromEditor(form: EditorModuleInput, rosterCount = 0): EventModules {
  const opts = form.formOptions;
  return {
    registration: form.registrationMode !== 'not_required',
    fees: Boolean(
      opts.paymentRequired ||
        opts.memberChoice ||
        filled(opts.paymentInstructions) ||
        filled(form.feeMember) ||
        filled(form.feeNonMember) ||
        filled(form.upiId) ||
        filled(form.upiQrUrl),
    ),
    questions: Boolean(
      opts.collectDob ||
        opts.collectGender ||
        form.teamEnabled ||
        form.notesEnabled ||
        filled(opts.notesQuestion) ||
        opts.minAgeYears != null,
    ),
    dropPoints: form.dropPoints.length > 0 || form.pickupEnabled,
    whatsapp: filled(opts.whatsappGroupUrl) || opts.whatsappGroupPublic === true,
    cta: form.ctaMode !== 'none' || filled(form.ctaLabel) || filled(form.ctaUrl),
    highlights:
      filled(form.coordinator) ||
      filled(form.specialMention) ||
      filled(form.participantCountOverride) ||
      filled(form.detailsUrl) ||
      filled(form.detailsLabel) ||
      filled(form.galleryAlbumId),
    roster: rosterCount > 0,
  };
}

/** Flags for sections that are open but still empty. Omitted when nothing needs remembering. */
export function persistedModuleFlags(form: EditorModuleInput, rosterCount = 0): EventModuleFlags | undefined {
  const implied = impliedModulesFromEditor(form, rosterCount);
  const extra: EventModuleFlags = {};
  for (const key of EVENT_MODULE_KEYS) {
    if (form.modules[key] && !implied[key]) extra[key] = true;
  }
  return Object.keys(extra).length > 0 ? extra : undefined;
}

export type ModuleUntick =
  | { action: 'allow' }
  | { action: 'confirm'; message: string }
  | { action: 'block'; message: string };

function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * Unticking never clears data by itself. Callers confirm, then `clearEditorModule`.
 * A roster with people is blocked: those rows are removed one by one, not by this checkbox.
 */
export function moduleUntickDecision(form: EditorModuleInput, key: EventModuleKey, rosterCount = 0): ModuleUntick {
  const implied = impliedModulesFromEditor(form, rosterCount);
  if (key === 'roster') {
    if (rosterCount > 0) {
      const people = rosterCount === 1 ? '1 person is' : `${rosterCount} people are`;
      return { action: 'block', message: `Remove everyone from the roster before turning this off. ${people} still on it.` };
    }
    return { action: 'allow' };
  }
  if (key === 'registration') {
    const parts: string[] = [];
    if (filled(form.deadlineAt)) parts.push('the deadline');
    if (filled(form.capacity)) parts.push('the capacity');
    if (implied.fees) parts.push('the fees and payment settings');
    if (implied.questions) parts.push('the extra form questions');
    if (parts.length === 0) return { action: 'allow' };
    return {
      action: 'confirm',
      message: `This will mark registration as not required and remove ${joinList(parts)}.`,
    };
  }
  if (!implied[key]) return { action: 'allow' };
  if (key === 'dropPoints') {
    const count = form.dropPoints.length;
    const parts: string[] = [];
    if (count > 0) parts.push(count === 1 ? '1 drop point' : `${count} drop points`);
    if (form.pickupEnabled) parts.push('pickup requests');
    return { action: 'confirm', message: `This will remove ${joinList(parts)}.` };
  }
  if (key === 'whatsapp') {
    return {
      action: 'confirm',
      message: filled(form.formOptions.whatsappGroupUrl)
        ? 'This will remove the WhatsApp group link.'
        : 'This will turn off the public WhatsApp group link.',
    };
  }
  const messages: Partial<Record<EventModuleKey, string>> = {
    fees: 'This will remove the fees and payment settings.',
    questions: 'This will remove the extra form questions.',
    cta: 'This will remove the app call to action.',
    highlights: 'This will remove the past-event highlights.',
  };
  return { action: 'confirm', message: messages[key] ?? 'This will remove the saved details for this section.' };
}

function clearedFees<T extends EditorModuleInput>(form: T): T {
  return {
    ...form,
    feeMember: '',
    feeNonMember: '',
    upiId: '',
    upiQrUrl: '',
    formOptions: {
      ...form.formOptions,
      paymentRequired: false,
      memberChoice: false,
      paymentInstructions: null,
    },
  };
}

function clearedQuestions<T extends EditorModuleInput>(form: T): T {
  return {
    ...form,
    teamEnabled: false,
    notesEnabled: false,
    formOptions: {
      ...form.formOptions,
      collectDob: false,
      collectGender: false,
      teamSize: null,
      notesQuestion: null,
      notesRequired: false,
      minAgeYears: null,
      ageAsOn: null,
    },
  };
}

/** Clears one section after the admin confirms. Registration also clears fees and questions. */
export function clearEditorModule<T extends EditorModuleInput>(form: T, key: EventModuleKey): T {
  const modules = { ...form.modules, [key]: false };
  if (key === 'registration') {
    const next = clearedQuestions(clearedFees({ ...form, modules, registrationMode: 'not_required', deadlineAt: '', capacity: '' }));
    next.modules = { ...next.modules, registration: false, fees: false, questions: false };
    return next;
  }
  if (key === 'fees') return clearedFees({ ...form, modules });
  if (key === 'questions') return clearedQuestions({ ...form, modules });
  if (key === 'dropPoints') return { ...form, modules, dropPoints: [], pickupEnabled: false };
  if (key === 'whatsapp') {
    return {
      ...form,
      modules,
      formOptions: { ...form.formOptions, whatsappGroupUrl: null, whatsappGroupPublic: false },
    };
  }
  if (key === 'cta') return { ...form, modules, ctaMode: 'none', ctaLabel: '', ctaUrl: '' };
  if (key === 'highlights') {
    return {
      ...form,
      modules,
      coordinator: '',
      specialMention: '',
      participantCountOverride: '',
      detailsUrl: '',
      detailsLabel: '',
      galleryAlbumId: '',
    };
  }
  return { ...form, modules };
}
